import React, { useState, useEffect } from 'react';
import {
  Monitor,
  Database,
  BookOpen,
  LogOut,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Map,
  Settings,
  Users,
  School,
  LayoutDashboard,
  ShieldCheck
} from 'lucide-react';

const Sidebar = ({
  user,
  abaAtiva,
  setAbaAtiva,
  sidebarAberta,
  setSidebarAberta,
  onLogout
}) => {
  const [categoriasAbertas, setCategoriasAbertas] = useState({
    monitoramento: true,
    suporte: true,
    admin: true
  });

  // 🔥 Fecha automaticamente ao trocar para desktop/mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarAberta(true);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setSidebarAberta]);

  const toggleCategoria = (cat) => {
    if (!sidebarAberta) {
      setSidebarAberta(true);
      return;
    }

    setCategoriasAbertas((prev) => ({
      ...prev,
      [cat]: !prev[cat]
    }));
  };

  // 🔥 Corrigido fechamento mobile
  const navegaPara = (aba) => {
    setAbaAtiva(aba);

    // Fecha imediatamente no mobile
    if (window.innerWidth < 1024) {
      setTimeout(() => {
        setSidebarAberta(false);
      }, 50);
    }
  };

  const temas = {
    monitoramento: {
      text: 'text-emerald-500',
      bg: 'bg-emerald-600'
    },
    suporte: {
      text: 'text-blue-500',
      bg: 'bg-blue-600'
    },
    admin: {
      text: 'text-purple-500',
      bg: 'bg-purple-600'
    }
  };

  const MenuItem = ({ id, icon: Icon, label, categoria }) => {
    const ativo = abaAtiva === id;
    const tema = temas[categoria];

    return (
      <button
        onClick={() => navegaPara(id)}
        className={`
          w-full flex items-center rounded-2xl font-black text-[10px]
          tracking-widest transition-all duration-300 mb-1

          ${sidebarAberta
            ? 'px-6 py-4 gap-4'
            : 'p-4 justify-center'}

          ${ativo
            ? `${tema.bg} text-white shadow-lg shadow-black/20`
            : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800/50'}
        `}
      >
        <Icon
          size={18}
          className={ativo ? 'text-white' : tema.text}
        />

        {sidebarAberta && (
          <span className="uppercase">
            {label}
          </span>
        )}
      </button>
    );
  };

  const CategoriaHeader = ({
    id,
    label,
    icon: Icon
  }) => {
    const tema = temas[id];

    return (
      <div
        onClick={() => toggleCategoria(id)}
        className={`
          flex items-center justify-between cursor-pointer
          p-4 group transition-all mt-2

          ${!sidebarAberta && 'justify-center'}
        `}
      >
        <div className="flex items-center gap-3">
          <div
            className={`
              p-1.5 rounded-lg
              bg-slate-100 dark:bg-slate-800/50
              ${tema.text}
            `}
          >
            <Icon size={14} />
          </div>

          {sidebarAberta && (
            <span
              className={`
                text-[9px] font-black uppercase
                tracking-[0.2em] ${tema.text}
              `}
            >
              {label}
            </span>
          )}
        </div>

        {sidebarAberta && (
          <ChevronDown
            size={14}
            className={`
              text-slate-500 transition-transform duration-300
              ${categoriasAbertas[id] ? 'rotate-180' : ''}
            `}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {/* OVERLAY MOBILE */}
      {sidebarAberta && (
        <div
          className="
            fixed inset-0
            bg-black/70
            backdrop-blur-sm
            z-[999]
            lg:hidden
            animate-in fade-in duration-300
          "
          onClick={() => setSidebarAberta(false)}
        />
      )}

      {/* SIDEBAR */}
      <aside
        className={`
          fixed top-0 left-0
          h-screen
          z-[1000]

          bg-white dark:bg-slate-900
          border-r border-slate-200 dark:border-slate-800

          flex flex-col
          transition-all duration-300 ease-in-out

          lg:sticky

          ${
            sidebarAberta
              ? 'w-72 translate-x-0'
              : '-translate-x-full lg:translate-x-0 lg:w-20'
          }
        `}
      >
        {/* HEADER */}
        <div
          className={`
            p-6 border-b
            border-slate-200 dark:border-slate-800
            text-emerald-500
            flex items-center

            ${sidebarAberta
              ? 'justify-between'
              : 'justify-center'}
          `}
        >
          <div
            className={`
              flex items-center gap-3
              ${!sidebarAberta && 'hidden'}
            `}
          >
            <Monitor
              size={28}
              className="animate-pulse"
            />

            <h1 className="text-2xl font-black tracking-tighter italic">
              CTC-IT
            </h1>
          </div>

          <button
            onClick={() =>
              setSidebarAberta(!sidebarAberta)
            }
            className="
              text-slate-400
              hover:text-emerald-500
              transition-colors
            "
          >
            {sidebarAberta ? (
              <>
                <ChevronLeft
                  size={24}
                  className="hidden lg:block"
                />

                <X
                  size={24}
                  className="lg:hidden"
                />
              </>
            ) : (
              <ChevronRight
                size={24}
                className="hidden lg:block"
              />
            )}
          </button>
        </div>

        {/* NAV */}
        <nav
          className={`
            flex-1 p-2
            space-y-1
            overflow-y-auto

            ${!sidebarAberta && 'hidden lg:block'}
          `}
        >
          {/* MONITORAMENTO */}
          <CategoriaHeader
            id="monitoramento"
            label="Monitoramento"
            icon={LayoutDashboard}
          />

          <div
            className={`
              overflow-hidden transition-all duration-500

              ${
                categoriasAbertas.monitoramento &&
                sidebarAberta
                  ? 'max-h-96 opacity-100 mt-2'
                  : 'max-h-0 opacity-0'
              }
            `}
          >
            <MenuItem
              id="dashboard"
              icon={Database}
              label="Dashboard"
              categoria="monitoramento"
            />

            <MenuItem
              id="mapa"
              icon={Map}
              label="Mapa Unidades"
              categoria="monitoramento"
            />
          </div>

          <div className="h-px bg-slate-100 dark:bg-slate-800/50 mx-4 my-2" />

          {/* SUPORTE */}
          <CategoriaHeader
            id="suporte"
            label="Suporte Técnico"
            icon={BookOpen}
          />

          <div
            className={`
              overflow-hidden transition-all duration-500

              ${
                categoriasAbertas.suporte &&
                sidebarAberta
                  ? 'max-h-40 opacity-100 mt-2'
                  : 'max-h-0 opacity-0'
              }
            `}
          >
            <MenuItem
              id="procedimentos"
              icon={ShieldCheck}
              label="Procedimentos"
              categoria="suporte"
            />
          </div>

          {/* ADMIN */}
          {['admin', 'superadmin'].includes(user?.perfil) && (
            <>
              <div className="h-px bg-slate-100 dark:bg-slate-800/50 mx-4 my-2" />

              <CategoriaHeader
                id="admin"
                label="Administração"
                icon={Settings}
              />

              <div
                className={`
                  overflow-hidden transition-all duration-500

                  ${
                    categoriasAbertas.admin &&
                    sidebarAberta
                      ? 'max-h-96 opacity-100 mt-2'
                      : 'max-h-0 opacity-0'
                  }
                `}
              >
                <MenuItem
                  id="admin_procedimentos"
                  icon={Settings}
                  label="Gerenciar Dados"
                  categoria="admin"
                />

                {user?.perfil === 'superadmin' && (
                  <>
                    <MenuItem
                      id="admin_escolas"
                      icon={School}
                      label="Gerenciar Escolas"
                      categoria="admin"
                    />

                    <MenuItem
                      id="admin_usuarios"
                      icon={Users}
                      label="Gestão de Equipe"
                      categoria="admin"
                    />
                  </>
                )}
              </div>
            </>
          )}
        </nav>

        {/* FOOTER */}
        <div
          className={`
            p-4 border-t
            border-slate-200 dark:border-slate-800/50

            ${!sidebarAberta && 'hidden lg:block'}
          `}
        >
          {sidebarAberta && user && (
            <div
              className="
                px-5 py-4 mb-4
                bg-slate-50 dark:bg-slate-800/40
                rounded-3xl
                border border-slate-100 dark:border-slate-800
              "
            >
              <p className="text-[8px] font-black text-slate-500 uppercase tracking-widest mb-1">
                Perfil Ativo
              </p>

              <p className="text-sm font-black text-emerald-500 truncate italic">
                {user.nome || 'Marcelo'}
              </p>

              <div className="flex items-center gap-2 mt-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />

                <p className="text-[9px] font-black text-slate-400 uppercase tracking-tighter">
                  {user.perfil}
                </p>
              </div>
            </div>
          )}

          <button
            onClick={onLogout}
            className={`
              w-full flex items-center
              text-red-500 font-black text-[10px]
              hover:bg-red-500/10
              rounded-2xl transition-all

              ${sidebarAberta
                ? 'px-6 py-5 gap-4'
                : 'p-4 justify-center'}
            `}
          >
            <LogOut size={20} />

            {sidebarAberta && (
              <span className="uppercase tracking-widest">
                Sair do Sistema
              </span>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;